export const CASH_OUTFLOW_CATEGORIES = [
  'supplier_payment',
  'owner_withdrawal',
  'supplies',
  'services',
  'other',
] as const;

export type CashOutflowCategory = (typeof CASH_OUTFLOW_CATEGORIES)[number];

export const CASH_OUTFLOW_CATEGORY_LABELS: Record<CashOutflowCategory, string> = {
  supplier_payment: 'Pago a proveedor',
  owner_withdrawal: 'Retiro del dueño',
  supplies: 'Insumos',
  services: 'Pago de servicios',
  other: 'Otro',
};

/** Categorías de ingreso manual de efectivo (par de los egresos). */
export const CASH_INFLOW_CATEGORIES = [
  'owner_contribution',
  'other_income',
] as const;

export type CashInflowCategory = (typeof CASH_INFLOW_CATEGORIES)[number];

export const CASH_INFLOW_CATEGORY_LABELS: Record<CashInflowCategory, string> = {
  owner_contribution: 'Aporte del dueño',
  other_income: 'Otro ingreso',
};

/** Dirección del movimiento manual de caja (server: pos.cash_outflows.direction). */
export type CashMovementDirection = 'out' | 'in';

export type CashMovementCategory = CashOutflowCategory | CashInflowCategory;

/** Labels unificados por categoría (egresos + ingresos) para listas/actividad. */
export const CASH_MOVEMENT_CATEGORY_LABELS: Record<CashMovementCategory, string> = {
  ...CASH_OUTFLOW_CATEGORY_LABELS,
  ...CASH_INFLOW_CATEGORY_LABELS,
};

export function isCashOutflowCategory(value: string): value is CashOutflowCategory {
  return (CASH_OUTFLOW_CATEGORIES as readonly string[]).includes(value);
}

export function isCashInflowCategory(value: string): value is CashInflowCategory {
  return (CASH_INFLOW_CATEGORIES as readonly string[]).includes(value);
}

export function isCashMovementCategory(value: string): value is CashMovementCategory {
  return isCashOutflowCategory(value) || isCashInflowCategory(value);
}

/**
 * Categorías válidas por dirección — espejo de la matriz server-side en
 * pos.apply_cash_outflow_command (category_invalid_for_direction).
 */
export function isCategoryValidForDirection(
  direction: CashMovementDirection,
  category: string,
): category is CashMovementCategory {
  return direction === 'in' ? isCashInflowCategory(category) : isCashOutflowCategory(category);
}

/** Categorías que exigen nota (una por dirección: 'other' / 'other_income'). */
export function categoryRequiresNote(direction: CashMovementDirection, category: string): boolean {
  return direction === 'in' ? category === 'other_income' : category === 'other';
}

export function validateCashMovementInput(input: {
  direction: CashMovementDirection;
  category: string;
  amount: number;
  note?: string;
}): { ok: true } | { ok: false; message: string } {
  if (!isCategoryValidForDirection(input.direction, input.category)) {
    return { ok: false, message: 'Elegí una categoría válida.' };
  }
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    return { ok: false, message: 'El monto debe ser mayor a cero.' };
  }
  if (categoryRequiresNote(input.direction, input.category) && !(input.note ?? '').trim()) {
    return { ok: false, message: 'Agregá una nota cuando la categoría es Otro.' };
  }
  return { ok: true };
}

export function validateCashOutflowInput(input: {
  category: string;
  amount: number;
  note?: string;
}): { ok: true } | { ok: false; message: string } {
  return validateCashMovementInput({ direction: 'out', ...input });
}
